import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import services from '../../services';

const AddContactModal = ({ showModal, handleClose, onContactAdded,sessionId }) => {
    const [contactData, setContactData] = useState({
        // name: '',
        sessionId:sessionId,
        to: ''
    });
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCountries, setFilteredCountries] = useState([]);

    // Update sessionId when prop changes
    useEffect(() => {
        setContactData(prev => ({
            ...prev,
            sessionId: sessionId
        }));
    }, [sessionId]);

    // Fetch countries on component mount
    useEffect(() => {
        fetchCountries();
    }, []);

    // Handle clicking outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showCountryDropdown && !event.target.closest('.country-dropdown')) {
                setShowCountryDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCountryDropdown]);

    // Filter countries based on search term
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredCountries(countries);
        } else {
            const filtered = countries.filter(country => 
                country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                country.dialCode.includes(searchTerm) ||
                country.code.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredCountries(filtered);
        }
    }, [searchTerm, countries]);

    const fetchCountries = async () => {
        try {
            // Using a free API to get country codes
            const response = await fetch('https://restcountries.com/v3.1/all?fields=name,idd,flag');
            const data = await response.json();
            
            const formattedCountries = data
                .filter(country => country.idd?.root) 
                .map(country => ({
                    name: country.name.common,
                    code: country.idd.root + (country.idd.suffixes?.[0] || ''),
                    flag: country.flag,
                    dialCode: country.idd.root + (country.idd.suffixes?.[0] || '')
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            setCountries(formattedCountries);
            setFilteredCountries(formattedCountries);
            
            // Set default country (India)
            const defaultCountry = formattedCountries.find(c => c.code === '+91') || formattedCountries[0];
            setSelectedCountry(defaultCountry);
        } catch (error) {
            console.error('Error fetching countries:', error);

            const fallbackCountries = [
                { name: 'India', code: '+91', flag: '🇮🇳', dialCode: '+91' },
                { name: 'United States', code: '+1', flag: '🇺🇸', dialCode: '+1' },
                { name: 'United Kingdom', code: '+44', flag: '🇬🇧', dialCode: '+44' },
                { name: 'Canada', code: '+1', flag: '🇨🇦', dialCode: '+1' },
                { name: 'Australia', code: '+61', flag: '🇦🇺', dialCode: '+61' },
                { name: 'Germany', code: '+49', flag: '🇩🇪', dialCode: '+49' },
                { name: 'France', code: '+33', flag: '🇫🇷', dialCode: '+33' },
                { name: 'Japan', code: '+81', flag: '🇯🇵', dialCode: '+81' },
                { name: 'China', code: '+86', flag: '🇨🇳', dialCode: '+86' },
                { name: 'Brazil', code: '+55', flag: '🇧🇷', dialCode: '+55' }
            ];
            setCountries(fallbackCountries);
            setFilteredCountries(fallbackCountries);
            setSelectedCountry({ name: 'India', code: '+91', flag: '🇮🇳', dialCode: '+91' });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setContactData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePhoneNumberChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
        setPhoneNumber(value);
        
        // Update the contactData with full phone number including country code
        const fullNumber = selectedCountry ? selectedCountry.dialCode + value : value;
        setContactData(prev => ({
            ...prev,
            to: fullNumber
        }));
    };

    const selectCountry = (country) => {
        setSelectedCountry(country);
        setShowCountryDropdown(false);
        setSearchTerm(''); // Clear search when country is selected
        
        // Update the contactData with new country code
        const fullNumber = country.dialCode + phoneNumber;
        setContactData(prev => ({
            ...prev,
            to: fullNumber
        }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const toggleCountryDropdown = () => {
        if (!showCountryDropdown) {
            setSearchTerm(''); // Clear search when opening dropdown
        }
        setShowCountryDropdown(!showCountryDropdown);
    };

    const handleSubmit = async () => {
        if (!contactData.to.trim()) {
            toast.warning('Please enter Contact number');
            return;
        }
        setLoading(true);
        try {
            // First, add the contact
            const response = await services.Whatsapp.CREATE_NEW_CONTACT(contactData);
            if (response) {
                // Extract the phone number from the response
                const sentTo = response.data?.sentTo || response.data?.data?.sentTo;
                if (sentTo) {
                    try {
                        const contactsResponse = await services.Whatsapp.GET_CONTACT_WHATSAPP();
                        if (contactsResponse?.data?.data) {
                            const matchingContact = contactsResponse?.data?.data?.find(contact =>
                                contact.contactNo == sentTo
                            );
                            if (matchingContact) {
                                // Create the new contact object with the found ID
                                const newContact = {
                                    value: matchingContact.id,
                                    label: matchingContact.name || matchingContact.contactNo,
                                    // phone: matchingContact.contactNo.replace('@c.us', '')
                                };
                                setContactData({ sessionId: sessionId, to: '' });
                                setPhoneNumber('');
                                if (onContactAdded) {
                                    onContactAdded(newContact);
                                }
                                handleClose();
                                toast.success('Contact added successfully!');
                            } else {
                                // // If no matching contact found, create with phone number as value
                                // const newContact = {
                                //     value: phoneNumber,
                                //     label: phoneNumber,
                                //     phone: phoneNumber
                                // };
                                setContactData({ sessionId: sessionId, to: '' });
                                setPhoneNumber('');
                                if (onContactAdded) {
                                    onContactAdded(newContact);
                                }
                                handleClose();
                                toast.success('Contact added successfully!');
                            }
                        } else {
                            // Fallback if contacts API fails
                            const newContact = {
                                value: phoneNumber,
                                label: phoneNumber,
                                phone: phoneNumber
                            };
                            setContactData({ sessionId: sessionId, to: '' });
                            setPhoneNumber('');
                            if (onContactAdded) {
                                onContactAdded(newContact);
                            }
                            handleClose();
                            toast.success('Contact added successfully!');
                        }
                    } catch (contactsError) {
                        console.error('Error fetching contacts:', contactsError);
                        const newContact = {
                            value: phoneNumber,
                            label: phoneNumber,
                            phone: phoneNumber
                        };
                        setContactData({ sessionId: sessionId, to: '' });
                        setPhoneNumber('');
                        if (onContactAdded) {
                            onContactAdded(newContact);
                        }
                        handleClose();
                        toast.success('Contact added successfully!');
                    }
                } else {
                    // Fallback if no sentTo in response
                    const newContact = {
                        value: contactData.to,
                        label: contactData.to,
                        phone: contactData.to
                    };
                    setContactData({ sessionId: sessionId, to: '' });
                    setPhoneNumber('');
                    if (onContactAdded) {
                        onContactAdded(newContact);
                    }
                    handleClose();
                    toast.success('Contact added successfully!');
                }
            }
        } catch (error) {
            console.error('Error adding contact:', error);
            toast.error('Failed to add contact. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    if (!showModal) return null;

    return (
        <div
            className="modal fade show"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="addContactModalTitle"
            aria-hidden="true"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header flex !items-center !justify-between">
                        <h5 className="modal-title" id="addContactModalTitle">
                            Add New Contact
                        </h5>
                        <button
                            type="button"
                            className="close custom-close"
                            onClick={handleClose}
                            aria-label="Close"
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    
                    <div className="modal-body">
                        <div style={{ marginTop: '15px' }}>
                            <label className="block mb-[0.4rem] text-gray-600 text-sm font-i">
                                Phone Number *
                            </label>
                            <div className="flex">
                                {/* Country Code Selector */}
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={toggleCountryDropdown}
                                        className="flex items-center px-3 py-2 border border-gray-300 rounded-l-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        style={{ minWidth: '80px' }}
                                    >
                                        <span className="mr-2">{selectedCountry?.flag}</span>
                                        <span>{selectedCountry?.dialCode}</span>
                                        <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    
                                    {showCountryDropdown && (
                                        <div className="country-dropdown absolute z-10 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            <div className="p-2">
                                                <input
                                                    type="text"
                                                    placeholder="Search countries..."
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                    onChange={handleSearchChange}
                                                />
                                            </div>
                                            {filteredCountries.map((country) => (
                                                <button
                                                    key={country.code}
                                                    onClick={() => selectCountry(country)}
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center"
                                                >
                                                    <span className="mr-3">{country.flag}</span>
                                                    <span className="flex-1">{country.name}</span>
                                                    <span className="text-gray-500">{country.dialCode}</span>
                                                </button>
                                            ))}
                                            {filteredCountries.length === 0 && searchTerm.trim() !== '' && (
                                                <div className="px-4 py-2 text-gray-500 text-sm">
                                                    No countries found matching "{searchTerm}"
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Phone Number Input */}
                                <input
                                    type="tel"
                                    name="phoneNumber"
                                    className="flex-1 h-10 border-l-0 border-r text-gray-900 font-i focus:outline-none p-3 border-i input-border rounded-r-md"
                                    value={phoneNumber}
                                    onChange={handlePhoneNumberChange}
                                    placeholder="Enter phone number"
                                    onKeyPress={handleKeyPress}
                                />
                            </div>
                            
                         
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Adding...' : 'Add Contact'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddContactModal; 